import { trackEvent } from "../analytics/analytics";
import apiClient, {
  OperationResult,
  BaseOperationResult,
} from "../../services/apiClient";
import {
  bumpImageVersion,
  BlobImage,
  ImageDb,
  ImageDbRaw,
  ROOT_FOLDER,
  parseImagePath,
  ImageInfo,
  combinePath,
} from "./image";

const listFile = ".list";

export const getImageDb = async (storageUrl: string): Promise<ImageDb> => {
  const response = await apiClient.get<ImageDbRaw>("/images/list");
  return transformImageDb(response.data, storageUrl);
};

const removeImage = (db: ImageDb, path: string) => {
  const p = parseImagePath(db.storageUrl, path);
  const folderKey = p.folder || ROOT_FOLDER;
  const m = db.folderMap.get(folderKey);
  if (m) {
    const i = m.files.findIndex((b) => b.path === path);
    if (i >= 0) {
      const f = m.files[i];
      m.files.splice(i, 1);
      m.size -= f.size;
      db.size -= f.size;
      db.folderMap.set(folderKey, { ...m });
    }
  }
  db.imageMap.delete(path);
};

const pushImage = (db: ImageDb, i: ImageInfo) => {
  const p = parseImagePath(db.storageUrl, i.p);
  const b: BlobImage = {
    path: i.p,
    size: i.l,
    modified: i.m ?? new Date().toISOString(),
    type: i.t, // Type from backend ("image" or "document")
    ...p,
  };
  const f = p.folder || ROOT_FOLDER;
  if (!db.folderMap.get(f)) {
    db.folderMap.set(f, { files: [], size: 0 });
  }
  if (b.name !== listFile) {
    const ff = db.folderMap.get(f);
    ff.files.push(b);
    ff.size += b.size;
    db.size += b.size;
    db.imageMap.set(i.p, b);
  }
  return b;
};

const transformImageDb = (dbRaw: ImageDbRaw, storageUrl: string): ImageDb => {
  const db: ImageDb = {
    imageMap: new Map(),
    folderMap: new Map(),
    storageUrl,
    size: 0,
    version: 0,
  };

  dbRaw.images.forEach((i) => {
    pushImage(db, i);
  });

  return db;
};

/**
 * Builds the backup zip's filename: `images[-<scope>]-YYYY-MM-DD-HHMM.zip`.
 * The date+time keeps repeated backups distinct (browsers otherwise append
 * "(1)"), which matters for a backup people run more than once; the optional
 * scope (folder name or "selected") says what's inside at a glance.
 */
const zipFileName = (scope?: string): string => {
  const now = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  const stamp = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(
    now.getDate()
  )}-${p(now.getHours())}${p(now.getMinutes())}`;
  const slug = scope
    ? "-" +
      scope
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
    : "";
  return `images${slug}-${stamp}.zip`;
};

/**
 * Downloads the account's images as a zip backup. Pass `paths` to export only a
 * selection, omit it for everything. `scope` (folder name or "selected") is woven
 * into the filename so the download says what it holds.
 *
 * Posts the selection rather than putting it in the query string: a "select all →
 * download" of a few hundred images exceeds the server's 8KB request-line limit.
 */
export const downloadImagesZip = async (paths?: string[], scope?: string) => {
  trackEvent({
    category: "image",
    action: "download-zip",
    value: paths?.length ?? 0,
    label: scope ?? "all",
    feature: true,
  });

  const config = {
    responseType: "blob" as const,
    // The zip streams for as long as the account is big — the client's 60s
    // default would abort a large backup mid-stream.
    timeout: 0,
  };
  const response = paths?.length
    ? await apiClient.post("/images/download-all", { paths }, config)
    : await apiClient.get("/images/download-all", config);

  const link = document.createElement("a");
  link.style.display = "none";
  link.href = URL.createObjectURL(response.data as Blob);
  link.download = zipFileName(scope);
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.parentNode.removeChild(link);
  }, 0);
};

export const copyDb = (db: ImageDb): ImageDb => ({
  ...db,
  imageMap: new Map(db.imageMap),
  folderMap: new Map(db.folderMap),
});

/**
 * The one client-side funnel for "an image was just written" - the upload modal, the Concierge and
 * image-func all land here.
 *
 * S72: bump the cache token when the path ALREADY existed, i.e. this replaced something. Without
 * it the app keeps requesting the previous `?v=`, so a re-upload shows the old picture until the
 * session reloads - which is exactly what Andriy hit on 2026-08-30 ("I reuploaded and saw the wrong
 * copy but after image purge all is fine"; purging worked only because that path bumps too).
 *
 * Gated on the path already being known, mirroring the server's overwrite-only rule: a brand-new
 * image cannot be stale, and bumping for it would needlessly re-fetch every other image on screen -
 * which on a 50-file bulk upload is 50 pointless invalidations.
 */
export const addImageToDb = (db: ImageDb, path, length, oldPath?): ImageDb => {
  if (db.imageMap.has(oldPath ?? path)) bumpImageVersion();
  const newDb = copyDb(db);
  removeImage(newDb, oldPath ?? path);
  pushImage(newDb, { p: path, l: length });
  return newDb;
};

export const moveImagesDb = async (
  db: ImageDb,
  paths: string[],
  folder: string
): Promise<ImageDb> => {
  trackEvent({
    category: "image",
    action: "move",
    value: paths.length,
    label: folder,
  });
  const response = await apiClient.post<OperationResult<string[]>>(
    "/images/move",
    {
      paths,
      folder: folder === ROOT_FOLDER ? "" : folder,
    }
  );
  const moved = response.data?.value;
  if (!moved?.length) return db;

  const newDb = copyDb(db);
  moved.forEach((path) => {
    const i = newDb.imageMap.get(path);
    const newPath = combinePath(folder, i.name);
    removeImage(newDb, path);
    pushImage(newDb, { p: newPath, l: i.size });
  });
  return newDb;
};

export const deleteImagesDb = async (
  db: ImageDb,
  paths: string[]
): Promise<ImageDb> => {
  const response = await apiClient.post<OperationResult<string[]>>(
    "/images/delete",
    { paths }
  );
  const deleted = response.data?.value;

  if (!deleted?.length) return db;

  // S72 - the server just moved this account's cache token. Bump the client's copy so anything
  // rendered before the next session load already asks for a URL nothing has cached.
  bumpImageVersion();

  const newDb = copyDb(db);
  deleted.forEach((path) => {
    removeImage(newDb, path);
  });
  return newDb;
};

/**
 * S72 - "Refresh CDN copies". Purges this account's images at the Cloudflare edge, the whole
 * library or one folder.
 *
 * The manual escape hatch. Everything rendered from S72 forward carries a `?v=` token and cannot
 * go stale, but URLs published BEFORE it shipped are bare and always will be - every live catalog
 * and every sent email quotes one - and a purge is the only lever on those.
 *
 * ⚠ It clears the CDN, not a visitor's browser. Nothing in the UI may promise otherwise.
 */
export const purgeImageCache = async (folder?: string) => {
  trackEvent({
    category: "image",
    action: "purge-cache",
    label: folder || "all",
    feature: true,
  });
  const response = await apiClient.post<BaseOperationResult>(
    "/images/purge-cache",
    { folder }
  );
  bumpImageVersion();
  return response.data;
};

export const addFolderDb = async (
  db: ImageDb,
  folder: string
): Promise<ImageDb> => {
  trackEvent({
    category: "image",
    action: "folder",
    label: folder,
    feature: true,
  });
  const response = await apiClient.post<BaseOperationResult>(
    "/images/add-folder",
    { folder }
  );

  if (!response.data.success) return db;

  const newDb = copyDb(db);
  newDb.folderMap.set(folder, { files: [], size: 0 });
  return newDb;
};

export const deleteFolderDb = async (
  db: ImageDb,
  folder: string
): Promise<ImageDb> => {
  trackEvent({ category: "image", action: "delete-folder", label: folder });
  const response = await apiClient.post<BaseOperationResult>(
    "/images/delete-folder",
    { folder }
  );

  if (!response.data.success) return db;

  const newDb = copyDb(db);

  const m = newDb.folderMap.get(folder);
  if (m) {
    m.files.forEach((f) => {
      newDb.size -= f.size;
      newDb.imageMap.delete(f.path);
    });
    newDb.folderMap.delete(folder);
  }
  return newDb;
};

export const renameImageDb = async (
  db: ImageDb,
  path: string,
  newName: string
): Promise<ImageDb> => {
  // Server trims too — also trim here so the optimistic local update reflects
  // the same blob name the server will store.
  newName = newName.trim();
  trackEvent({
    category: "image",
    action: "rename",
    label: path + " > " + newName,
  });
  const response = await apiClient.post<BaseOperationResult>("/images/rename", {
    path,
    newName,
  });
  if (!response.data.success) return db;

  const newDb = copyDb(db);
  const i = newDb.imageMap.get(path);
  const newPath = combinePath(i.folder, newName);
  removeImage(newDb, path);
  pushImage(newDb, { p: newPath, l: i.size, m: new Date().toISOString() });
  return newDb;
};
